// The Events Calendar's consumer-facing model (CR-20260904-001, contract C1).
//
// This is what ORION's filtering and the Price Box colouring read. It is deliberately NOT the
// wire shape: the wire calls the day fields "...DayKey" so the query-path date reviver leaves
// them alone, and LandmineDaysService maps them onto the contract's names below. Everything here
// is a plain "yyyy-MM-dd" LABEL - never a Date, never an instant.
//
// Pure / Angular-free so it stays trivially testable and carries no module dependency.
// ES5 note: arrays + indexOf only (no Set / for..of), matching the rest of the copilot logic.

import { expirationDayKey } from '../../copilot-shared/expiration-dates';

// --- closed vocabularies (C1 section 2) --------------------------------------------------------
// A value outside these is a provider defect, not a new case. Consumers must never widen them.

export type LandmineTier = 'T1' | 'T2' | 'clear' | 'unknown';
export type LandmineEventKind = 'FOMC' | 'CPI' | 'NFP' | 'Earnings';
export type LandmineSession = 'pre-market' | 'after-close';
export type LandmineCoverageStatus = 'ok' | 'partial' | 'unavailable';

export const LANDMINE_TIERS: string[] = ['T1', 'T2', 'clear', 'unknown'];
export const LANDMINE_EVENT_KINDS: string[] = ['FOMC', 'CPI', 'NFP', 'Earnings'];
export const LANDMINE_SESSIONS: string[] = ['pre-market', 'after-close'];

// --- the verdict record (C1 section 2) ---------------------------------------------------------

export interface LandmineEvent {
    /** Closed vocabulary (BR-D7). */
    kind: LandmineEventKind;
    /** Mag-7 ticker, earnings only. Absent (null) for reports. GOOG is reported as GOOGL. */
    ticker?: string;
    /** "yyyy-MM-dd" - the day the release happens. */
    releaseDate: string;
    /** "yyyy-MM-dd" - the trading day the market reacts. Equal to the row's `date` when the
     *  event bites on this row; otherwise it is a cluster member listed for explanation. */
    effectDate: string;
    /** Explanatory only - the arithmetic is already in `effectDate`. */
    session: LandmineSession;
    /** Published (true) or estimated (false). */
    confirmed: boolean;
}

export interface LandmineDay {
    /** "yyyy-MM-dd" - the market-session date this verdict is about. Joins onto the option
     *  chain's optionExpirationDate / expirationDayKey(). */
    date: string;
    /** Whether the market opens that day. False for weekends and exchange holidays - and for an
     *  "unknown" row, because a day the calendar cannot vouch for is never a candidate. */
    tradingDay: boolean;
    /** The verdict, already resolved to the STRONGEST tier. The only authority for
     *  disqualification and for colour. "unknown" is never "clear". */
    tier: LandmineTier;
    /** True when at least one event behind this verdict rests on an unconfirmed date. Carried
     *  for a future ruling; today every consumer treats estimated exactly as confirmed. */
    estimated: boolean;
    /** Every named event that contributed to this row's verdict. Read for WORDING only -
     *  never re-derive a tier from them. */
    events: LandmineEvent[];
}

export interface LandmineCoverage {
    from: string;
    to: string;
}

/**
 * The answer for one span. `get` is a synchronous read and returns a record for EVERY date in
 * [from, to]; a key outside the requested span returns undefined, which callers treat as a
 * programming error, not as "clear".
 *
 * Identity matters: the same span asked twice inside the cache lifetime yields the SAME instance,
 * so the ORION card and the Price Box column cannot disagree (C1 G7).
 */
export interface LandmineDayMap {
    readonly id: string;
    readonly asOf: string;
    readonly coverage: LandmineCoverage;
    readonly status: LandmineCoverageStatus;
    get(dateKey: string): LandmineDay | undefined;
    /** Every date key this map answers, in ascending order. */
    keys(): string[];
}

// --- day-key arithmetic ------------------------------------------------------------------------

/**
 * "yyyy-MM-dd" -> a local-midnight Date, or null when the label is not a real calendar date.
 * Local, not UTC: expirationDayKey() reads local Y/M/D, so a local-midnight Date maps back onto
 * its own label in every timezone.
 */
export function parseDayKey(dayKey: string): Date | null {
    if (typeof dayKey !== 'string') {
        return null;
    }

    const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dayKey);
    if (!parts) {
        return null;
    }

    const year = +parts[1];
    const month = +parts[2];
    const day = +parts[3];
    const date = new Date(year, month - 1, day);

    // Rejects rollovers such as "2026-02-31", which JS would silently accept.
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
        return null;
    }

    return date;
}

/** Guards a runaway walk. Ten years of calendar days - far past any Price Box or ORION span. */
const MAX_ENUMERATED_DAYS = 3660;

/**
 * Every calendar day key in [from, to] inclusive, ascending. Empty when either label is not a
 * date or the range is inverted. This is what lets the service guarantee a row for every date
 * the caller asked about, even when the backend answered with none.
 */
export function enumerateDayKeys(fromDayKey: string, toDayKey: string): string[] {
    const from = parseDayKey(fromDayKey);
    const to = parseDayKey(toDayKey);

    if (!from || !to || from.getTime() > to.getTime()) {
        return [];
    }

    const keys: string[] = [];
    const cursor = new Date(from.getFullYear(), from.getMonth(), from.getDate());

    while (cursor.getTime() <= to.getTime() && keys.length < MAX_ENUMERATED_DAYS) {
        keys.push(expirationDayKey(cursor));
        cursor.setDate(cursor.getDate() + 1);
    }

    return keys;
}

/**
 * The row a consumer gets for a date the calendar cannot vouch for. NOT "clear": ORION does not
 * offer an unknown day and the Price Box paints no colour on it (C1 open question Q2).
 * `tradingDay` is false because an unknown day is never a candidate.
 */
export function unknownLandmineDay(dateKey: string): LandmineDay {
    return {
        date: dateKey,
        tradingDay: false,
        tier: 'unknown',
        estimated: false,
        events: []
    };
}

// --- the map -----------------------------------------------------------------------------------

let mapSequence = 0;

class LandmineDayMapImpl implements LandmineDayMap {
    readonly id: string;

    private readonly _keys: string[];
    private readonly _byKey: { [dateKey: string]: LandmineDay };

    constructor(
        public readonly asOf: string,
        public readonly coverage: LandmineCoverage,
        public readonly status: LandmineCoverageStatus,
        days: LandmineDay[]
    ) {
        this._keys = [];
        this._byKey = {};

        const list = days || [];
        for (let i = 0; i < list.length; i++) {
            this._keys.push(list[i].date);
            this._byKey[list[i].date] = list[i];
        }

        // Identity of THIS instance, labelled with the span it answers - not with `coverage`,
        // which is what the store vouches for and can be wider or narrower than the question.
        mapSequence++;
        const span = this._keys.length > 0
            ? this._keys[0] + '..' + this._keys[this._keys.length - 1]
            : 'empty';
        this.id = 'lmd-' + mapSequence + '-' + span;
    }

    get(dateKey: string): LandmineDay | undefined {
        return this._byKey[dateKey];
    }

    keys(): string[] {
        return this._keys.slice();
    }
}

export function createLandmineDayMap(
    asOf: string,
    coverage: LandmineCoverage,
    status: LandmineCoverageStatus,
    days: LandmineDay[]
): LandmineDayMap {
    return new LandmineDayMapImpl(asOf, coverage, status, days);
}
